import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
  VStack,
  HStack,
  Button,
  Text,
  Box,
  Card,
  CardBody,
  SimpleGrid,
  useDisclosure,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  FormControl,
  FormLabel,
  Select,
  Input,
  useToast,
  Badge,
  IconButton,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useBreakpointValue,
  Skeleton,
} from '@chakra-ui/react';
import DataTable from 'react-data-table-component';
import { FiEdit, FiTrash2, FiPlus, FiRefreshCw } from 'react-icons/fi';
import { ieemPublishApi } from '../config/api';
import StatusChip from './bundling/StatusChip';
import ReadinessPanel from './bundling/ReadinessPanel';
import DataTableShell from './bundling/DataTableShell';
import SubheaderToolbar from './bundling/SubheaderToolbar';

const getPublishStatus = (item) => {
  const active = item.IEEMPUBLISH_STATUS === '1' || item.IEEMPUBLISH_STATUS === 1;
  return active ? 'Published' : 'Paused';
};

const getTypeLabel = (type) => {
  if (type === 'product') return 'Product';
  if (type === 'flyer') return 'Flyer';
  if (type === 'iframe') return 'Iframe';
  return type || '-';
};

const getPublishSummary = (rows) => {
  const total = rows.length;
  const active = rows.filter((row) => row.IEEMPUBLISH_STATUS === '1' || row.IEEMPUBLISH_STATUS === 1).length;
  const inactive = total - active;
  const uniqueTypes = new Set(rows.map((row) => row.IEEMPUBLISH_TYPE).filter(Boolean)).size;
  return { total, active, inactive, uniqueTypes };
};

const getPublishBlockers = (rows) => {
  const blockers = [];
  const summary = getPublishSummary(rows);
  if (summary.total === 0) blockers.push('Belum ada konfigurasi publish.');
  if (summary.active === 0 && summary.total > 0) blockers.push('Tidak ada publish aktif untuk member ini.');
  if (summary.uniqueTypes < 3) blockers.push('Coverage type belum lengkap (product, flyer, iframe).');
  return blockers;
};

const IeemPublishTab = ({ memberCode }) => {
  const [ieemPublishData, setIeemPublishData] = useState([]);
  const [loadingIeemPublish, setLoadingIeemPublish] = useState(false);
  const [editingIeemPublish, setEditingIeemPublish] = useState(null);
  const [deleteIeemPublish, setDeleteIeemPublish] = useState(null);
  const [pendingDeleteIeemPublish, setPendingDeleteIeemPublish] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const toast = useToast();
  const isMobileView = useBreakpointValue({ base: true, md: false });
  const deleteTimeoutRef = useRef(null);
  
  const { isOpen: isIeemPublishFormOpen, onOpen: onIeemPublishFormOpen, onClose: onIeemPublishFormClose } = useDisclosure();
  const { isOpen: isIeemPublishDeleteOpen, onOpen: onIeemPublishDeleteOpen, onClose: onIeemPublishDeleteClose } = useDisclosure();
  const cancelRef = React.useRef();

  // IEEM Publish form data
  const [ieemPublishFormData, setIeemPublishFormData] = useState({
    IEMMPUBLISH_MEMBER: memberCode || '',
    IEEMPUBLISH_TYPE: '',
    IEEMPUBLISH_STATUS: '1',
  });

  // Fetch IEEM Publish data
  const fetchIeemPublish = useCallback(async () => {
    setLoadingIeemPublish(true);
    try {
      // Ambil data langsung dengan filter berdasarkan memberCode di API
      const response = await ieemPublishApi.get(`/list?member=${memberCode}`);
      if (response.data.status === 200) {
        setIeemPublishData(response.data.data || []);
      } else {
        toast({
          title: 'Error',
          description: response.data.message || 'Gagal mengambil data IEEM Publish',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error fetching IEEM Publish:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat mengambil data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    } finally {
      setLoadingIeemPublish(false);
    }
  }, [toast, memberCode]);

  useEffect(() => {
    fetchIeemPublish();
  }, [fetchIeemPublish]);

  useEffect(() => {
    return () => {
      if (deleteTimeoutRef.current) {
        clearTimeout(deleteTimeoutRef.current);
      }
    };
  }, []);

  // IEEM Publish handlers
  const handleIeemPublishInputChange = (e) => {
    const { name, value } = e.target;
    setIeemPublishFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleIeemPublishCreate = () => {
    setEditingIeemPublish(null);
    setIeemPublishFormData({
      IEMMPUBLISH_MEMBER: memberCode || '',
      IEEMPUBLISH_TYPE: '',
      IEEMPUBLISH_STATUS: '1',
    });
    onIeemPublishFormOpen();
  };

  const handleIeemPublishEdit = (item) => {
    setEditingIeemPublish(item);
    setIeemPublishFormData({
      IEMMPUBLISH_MEMBER: item.IEMMPUBLISH_MEMBER || memberCode || '',
      IEEMPUBLISH_TYPE: item.IEEMPUBLISH_TYPE || '',
      IEEMPUBLISH_STATUS: item.IEEMPUBLISH_STATUS?.toString() || '1',
    });
    onIeemPublishFormOpen();
  };

  const handleIeemPublishSubmit = async () => {
    if (!ieemPublishFormData.IEEMPUBLISH_TYPE) {
      toast({
        title: 'Validasi gagal',
        description: 'Type harus diisi',
        status: 'warning',
        duration: 3000,
        isClosable: true,
      });
      return;
    }

    try {
      if (editingIeemPublish) {
        // Update
        const response = await ieemPublishApi.post(`/update/${editingIeemPublish.IEEMPUBLISH_ID}`, ieemPublishFormData);
        if (response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'IEEM Publish berhasil diupdate',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onIeemPublishFormClose();
          fetchIeemPublish();
        }
      } else {
        // Create
        const response = await ieemPublishApi.post('/', ieemPublishFormData);
        if (response.data.status === 201 || response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'IEEM Publish berhasil dibuat',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          onIeemPublishFormClose();
          fetchIeemPublish();
        }
      }
    } catch (error) {
      console.error('Error saving IEEM Publish:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.message || 'Terjadi kesalahan saat menyimpan data',
        status: 'error',
        duration: 3000,
        isClosable: true,
      });
    }
  };

  const handleIeemPublishDeleteClick = (item) => {
    setDeleteIeemPublish(item);
    onIeemPublishDeleteOpen();
  };

  const handleIeemPublishDeleteConfirm = async () => {
    if (!deleteIeemPublish) return;
    if (pendingDeleteIeemPublish) {
      toast({
        title: 'Hapus sedang diproses',
        description: 'Selesaikan atau undo penghapusan sebelumnya terlebih dahulu.',
        status: 'warning',
        duration: 2500,
        isClosable: true,
      });
      return;
    }

    const candidate = deleteIeemPublish;
    onIeemPublishDeleteClose();
    setDeleteIeemPublish(null);
    setPendingDeleteIeemPublish(candidate);

    toast({
      duration: 5000,
      isClosable: true,
      position: 'top-right',
      render: ({ onClose }) => (
        <Box
          role="status"
          aria-live="polite"
          bg="white"
          border="0.5px solid"
          borderColor="orange.200"
          borderRadius="md"
          boxShadow="primeSm"
          p={3}
        >
          <VStack align="stretch" spacing={2}>
            <Text fontSize="sm" fontWeight="700">Penghapusan dijadwalkan</Text>
            <Text fontSize="xs" color="label.secondary">
              Data akan dinonaktifkan dalam 5 detik.
            </Text>
            <HStack justify="flex-end">
              <Button
                size="xs"
                variant="outline"
                onClick={() => {
                  if (deleteTimeoutRef.current) {
                    clearTimeout(deleteTimeoutRef.current);
                    deleteTimeoutRef.current = null;
                  }
                  setPendingDeleteIeemPublish(null);
                  onClose();
                  toast({
                    title: 'Penghapusan dibatalkan',
                    description: 'Data tetap aktif.',
                    status: 'success',
                    duration: 2200,
                    isClosable: true,
                  });
                }}
              >
                Undo
              </Button>
            </HStack>
          </VStack>
        </Box>
      ),
    });

    deleteTimeoutRef.current = setTimeout(async () => {
      try {
        const response = await ieemPublishApi.post(`/update/${candidate.IEEMPUBLISH_ID}`, {
          IEMMPUBLISH_MEMBER: candidate.IEMMPUBLISH_MEMBER,
          IEEMPUBLISH_TYPE: candidate.IEEMPUBLISH_TYPE,
          IEEMPUBLISH_STATUS: '0',
        });
        if (response.data.status === 200) {
          toast({
            title: 'Berhasil',
            description: 'IEEM Publish berhasil dihapus (status diubah menjadi not active)',
            status: 'success',
            duration: 3000,
            isClosable: true,
          });
          fetchIeemPublish();
        }
      } catch (error) {
        console.error('Error deleting IEEM Publish:', error);
        toast({
          title: 'Error',
          description: error.response?.data?.message || 'Terjadi kesalahan saat menghapus data',
          status: 'error',
          duration: 3000,
          isClosable: true,
        });
      } finally {
        setPendingDeleteIeemPublish(null);
        deleteTimeoutRef.current = null;
      }
    }, 5000);
  };

  // Custom styles for DataTable
  const customStyles = {
    headRow: {
      style: {
        backgroundColor: '#f7fafc',
        borderBottom: '1px solid #e2e8f0',
        fontWeight: '600',
      },
    },
    headCells: {
      style: {
        fontSize: '14px',
        fontWeight: '600',
        textTransform: 'none',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    cells: {
      style: {
        fontSize: '14px',
        paddingLeft: '16px',
        paddingRight: '16px',
      },
    },
    rows: {
      style: {
        minHeight: '60px',
        '&:not(:last-of-type)': {
          borderBottom: '1px solid #e2e8f0',
        },
      },
    },
  };

  // Pagination component options
  const paginationComponentOptions = {
    rowsPerPageText: 'Baris per halaman:',
    rangeSeparatorText: 'dari',
    selectAllRowsItem: true,
    selectAllRowsItemText: 'Semua',
  };

  // Filter publish data based on search term
  const filteredPublishData = useMemo(() => {
    if (!searchTerm) return ieemPublishData;
    const searchLower = searchTerm.toLowerCase();
    return ieemPublishData.filter((publish) => {
      return (
        (publish.IEEMPUBLISH_TYPE && publish.IEEMPUBLISH_TYPE.toLowerCase().includes(searchLower)) ||
        (publish.COMPANY_NAME && publish.COMPANY_NAME.toLowerCase().includes(searchLower)) ||
        (publish.IEEMPUBLISH_ID && publish.IEEMPUBLISH_ID.toString().includes(searchLower)) ||
        (publish.IEMMPUBLISH_MEMBER && publish.IEMMPUBLISH_MEMBER.toLowerCase().includes(searchLower))
      );
    });
  }, [ieemPublishData, searchTerm]);

  const publishSummary = useMemo(() => getPublishSummary(filteredPublishData), [filteredPublishData]);
  const publishBlockers = useMemo(() => getPublishBlockers(filteredPublishData), [filteredPublishData]);

  return (
    <>
      <VStack align="stretch" spacing={4} mb={6}>
        <SimpleGrid columns={{ base: 2, md: 4 }} spacing={3}>
          <Card borderRadius="14px" border="1px solid" borderColor="gray.100">
            <CardBody py={3}>
              <Text fontSize="xs" color="gray.500">Total Publish</Text>
              <Skeleton isLoaded={!loadingIeemPublish}>
                <Text fontSize="xl" fontWeight="700" color="gray.800" fontVariantNumeric="tabular-nums">
                  {publishSummary.total}
                </Text>
              </Skeleton>
            </CardBody>
          </Card>
          <Card borderRadius="14px" border="1px solid" borderColor="green.100" bg="green.50">
            <CardBody py={3}>
              <Text fontSize="xs" color="green.700">Active</Text>
              <Skeleton isLoaded={!loadingIeemPublish}>
                <Text fontSize="xl" fontWeight="700" color="green.800" fontVariantNumeric="tabular-nums">
                  {publishSummary.active}
                </Text>
              </Skeleton>
            </CardBody>
          </Card>
          <Card borderRadius="14px" border="1px solid" borderColor="orange.100" bg="orange.50">
            <CardBody py={3}>
              <Text fontSize="xs" color="orange.700">Not Active</Text>
              <Skeleton isLoaded={!loadingIeemPublish}>
                <Text fontSize="xl" fontWeight="700" color="orange.800" fontVariantNumeric="tabular-nums">
                  {publishSummary.inactive}
                </Text>
              </Skeleton>
            </CardBody>
          </Card>
          <Card borderRadius="14px" border="1px solid" borderColor="blue.100" bg="blue.50">
            <CardBody py={3}>
              <Text fontSize="xs" color="blue.700">Type Covered</Text>
              <Skeleton isLoaded={!loadingIeemPublish}>
                <Text fontSize="xl" fontWeight="700" color="blue.800" fontVariantNumeric="tabular-nums">
                  {publishSummary.uniqueTypes}
                </Text>
              </Skeleton>
            </CardBody>
          </Card>
        </SimpleGrid>

        <ReadinessPanel blockers={publishBlockers} />

        <HStack spacing={3} flexWrap="wrap">
          <Button
            leftIcon={<FiRefreshCw />}
            onClick={fetchIeemPublish}
            isLoading={loadingIeemPublish}
            variant="outline"
            colorScheme="brand"
            size={{ base: 'sm', lg: 'md' }}
          >
            Refresh
          </Button>
          <Button
            leftIcon={<FiPlus />}
            onClick={handleIeemPublishCreate}
            colorScheme="brand"
            size={{ base: 'sm', lg: 'md' }}
          >
            Tambah Publish
          </Button>
        </HStack>
      </VStack>

      <DataTableShell isLoading={loadingIeemPublish}>
      <DataTable
        columns={[
          {
            name: 'ID',
            selector: (row) => row.IEEMPUBLISH_ID,
            sortable: true,
            width: '80px',
            omit: isMobileView,
          },
          {
            name: 'Member',
            selector: (row) => row.COMPANY_NAME || row.IEMMPUBLISH_MEMBER || '-',
            sortable: true,
            cell: (row) => (
              <Text>
                {row.COMPANY_NAME || row.IEMMPUBLISH_MEMBER || '-'}
              </Text>
            ),
          },
          {
            name: 'Type',
            selector: (row) => row.IEEMPUBLISH_TYPE || '-',
            sortable: true,
            cell: (row) => {
              return (
                <Badge colorScheme="blue" fontSize={{ base: 'xs', lg: 'sm' }}>
                  {getTypeLabel(row.IEEMPUBLISH_TYPE)}
                </Badge>
              );
            },
          },
          {
            name: 'Status',
            selector: (row) => row.IEEMPUBLISH_STATUS,
            sortable: true,
            cell: (row) => (
              <StatusChip status={getPublishStatus(row)} />
            ),
          },
          {
            name: 'Aksi',
            cell: (row) => (
              <HStack spacing={1} justify="center">
                <IconButton
                  icon={<FiEdit />}
                  size={{ base: 'xs', lg: 'sm' }}
                  colorScheme="blue"
                  variant="ghost"
                  onClick={() => handleIeemPublishEdit(row)}
                  aria-label="Edit"
                />
                <IconButton
                  icon={<FiTrash2 />}
                  size={{ base: 'xs', lg: 'sm' }}
                  colorScheme="red"
                  variant="ghost"
                  onClick={() => handleIeemPublishDeleteClick(row)}
                  aria-label="Delete"
                />
              </HStack>
            ),
            ignoreRowClick: true,
            allowOverflow: true,
            button: true,
            width: '120px',
          },
        ]}
        data={filteredPublishData}
        progressPending={loadingIeemPublish}
        progressComponent={<Text py={8}>Loading...</Text>}
        noDataComponent={
          <Text py={8} color="gray.500" textAlign="center">
            Tidak ada data IEEM Publish
          </Text>
        }
        subHeader
        subHeaderComponent={
          <SubheaderToolbar
            searchValue={searchTerm}
            onSearchChange={setSearchTerm}
            searchPlaceholder="Cari publish (type, member, ID)..."
            count={filteredPublishData.length}
          />
        }
        pagination
        paginationPerPage={10}
        paginationRowsPerPageOptions={[10, 20, 30, 50, 100]}
        paginationComponentOptions={paginationComponentOptions}
        highlightOnHover
        pointerOnHover
        customStyles={customStyles}
        responsive
        dense
        striped
      />
      </DataTableShell>

      {/* IEEM Publish Form Modal */}
      <Modal isOpen={isIeemPublishFormOpen} onClose={onIeemPublishFormClose} size="md">
        <ModalOverlay />
        <ModalContent>
          <ModalHeader>
            {editingIeemPublish ? 'Edit IEEM Publish' : 'Tambah IEEM Publish Baru'}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <VStack spacing={4}>
              <FormControl>
                <FormLabel>Member</FormLabel>
                <Input
                  name="IEMMPUBLISH_MEMBER"
                  value={ieemPublishFormData.IEMMPUBLISH_MEMBER}
                  isReadOnly
                  bg="gray.50"
                />
              </FormControl>

              <FormControl isRequired>
                <FormLabel>Type</FormLabel>
                <Select
                  name="IEEMPUBLISH_TYPE"
                  value={ieemPublishFormData.IEEMPUBLISH_TYPE}
                  onChange={handleIeemPublishInputChange}
                  placeholder="Pilih type"
                >
                  <option value="">Pilih type</option>
                  <option value="product">Product</option>
                  <option value="flyer">Flyer</option>
                  <option value="iframe">Iframe</option>
                </Select>
              </FormControl>

              <FormControl>
                <FormLabel>Status</FormLabel>
                <Select
                  name="IEEMPUBLISH_STATUS"
                  value={ieemPublishFormData.IEEMPUBLISH_STATUS}
                  onChange={handleIeemPublishInputChange}
                >
                  <option value="1">Active</option>
                  <option value="0">Not Active</option>
                </Select>
              </FormControl>
            </VStack>
          </ModalBody>

          <ModalFooter>
            <Button variant="ghost" mr={3} onClick={onIeemPublishFormClose}>
              Batal
            </Button>
            <Button colorScheme="brand" onClick={handleIeemPublishSubmit}>
              {editingIeemPublish ? 'Update' : 'Simpan'}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* IEEM Publish Delete Confirmation Dialog */}
      <AlertDialog
        isOpen={isIeemPublishDeleteOpen}
        leastDestructiveRef={cancelRef}
        onClose={onIeemPublishDeleteClose}
      >
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Hapus IEEM Publish
            </AlertDialogHeader>

            <AlertDialogBody>
              Apakah Anda yakin ingin menghapus data dengan Type <strong>{deleteIeemPublish?.IEEMPUBLISH_TYPE === 'product' ? 'Product' : deleteIeemPublish?.IEEMPUBLISH_TYPE === 'flyer' ? 'Flyer' : deleteIeemPublish?.IEEMPUBLISH_TYPE === 'iframe' ? 'Iframe' : deleteIeemPublish?.IEEMPUBLISH_TYPE}</strong>?
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button ref={cancelRef} onClick={onIeemPublishDeleteClose}>
                Batal
              </Button>
              <Button colorScheme="red" onClick={handleIeemPublishDeleteConfirm} ml={3}>
                Hapus
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

export default IeemPublishTab;

